/* eslint-disable max-lines */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { COMMAND_PRIORITY_CRITICAL, TextNode } from 'lexical';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';

import { $createVariableNode } from './VariableNode';

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = `\\b[A-Z][^\\s${PUNCTUATION}]`;

const DocumentVariablesRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentVariablesRegex.PUNCTUATION;

const TRIGGERS = ['{'].join('');

// Chars we expect to see in a variable (non-space, non-punctuation).
const VALID_CHARS = `[^${TRIGGERS}${PUNC}\\s]`;

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  `(?:` +
  `\\.[ |$]|` + // E.g. "r. " in "Mr. Smith"
  ` |` + // E.g. " " in "Josh Duck"
  `[${PUNC}]|` + // E.g. "-' in "Salier-Hellendag"
  `)`;

const LENGTH_LIMIT = 75;

const AtSignVariablesRegex = new RegExp(
  `(^|\\s|\\()([${TRIGGERS}]((?:${VALID_CHARS}${VALID_JOINS}){0,${LENGTH_LIMIT}}))$`
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignVariablesRegexAliasRegex = new RegExp(
  `(^|\\s|\\()([${TRIGGERS}]((?:${VALID_CHARS}){0,${ALIAS_LENGTH_LIMIT}}))$`
);

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5;

function checkForAtSignVariables(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignVariablesRegex.exec(text);

  if (match === null) {
    match = AtSignVariablesRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignVariables(text, 0);
}

class VariableTypeaheadOption extends MenuOption {
  value: string;

  constructor(attrs: { value: string }) {
    super(attrs.value);
    this.value = attrs.value;
  }
}

function VariablesTypeaheadMenuItem({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: VariableTypeaheadOption;
}): JSX.Element {
  let className =
    'w-full whitespace-no-wrap text-left border-litlingo-gray-2 p-2 focus:outline-none hover:bg-litlingo-gray-1';
  if (isSelected) {
    className += ' bg-litlingo-gray-1';
  }
  return (
    <li
      aria-hidden
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={`typeahead-item-${index}`}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      <div className="flex flex-row gap-1">
        <span>{option.value}</span>
      </div>
    </li>
  );
}

type ComponentProps = {
  initialOptions: { value: string }[];
};

const NewVariablesPlugin: React.FC<ComponentProps> = ({ initialOptions }): JSX.Element | null => {
  const [editor] = useLexicalComposerContext();

  const [queryString, setQueryString] = useState<string | null>(null);

  const results = initialOptions.filter((variable) =>
    variable.value.toLowerCase().startsWith(queryString?.toLowerCase() || ''.toLowerCase())
  );

  const options = useMemo(
    () =>
      results
        .map((result) => new VariableTypeaheadOption(result))
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results]
  );

  const onSelectOption = useCallback(
    (
      selectedOption: VariableTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        const variableNode = $createVariableNode(selectedOption.value, selectedOption.value);
        if (nodeToReplace) {
          nodeToReplace.replace(variableNode);
        }
        variableNode.select();
        closeMenu();
      });
    },
    [editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<VariableTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      triggerFn={getPossibleQueryMatch}
      options={options}
      commandPriority={COMMAND_PRIORITY_CRITICAL}
      anchorClassName="z-100"
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
      ): JSX.Element | null =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <div
                className="max-w-fit text-body mt-1 rounded shadow-lg border border-litlingo-gray-2"
                style={{ width: 'fit-content' }}
              >
                <ul
                  className="max-w-fit rounded bg-white shadow-xs"
                  style={{ width: 'fit-content' }}
                >
                  {options.map((option, i: number) => (
                    <VariablesTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={(): void => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={(): void => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))}
                </ul>
              </div>,
              anchorElementRef.current
            )
          : null
      }
    />
  );
};

export default NewVariablesPlugin;
